import type { AutomationAccessDto } from '@tms/shared';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

/**
 * TestRun answered with an error, or could not be reached at all (status 0).
 * TestRun's own messages are written for people, so they are shown as they are.
 */
export class AutomationError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AutomationError';
    this.status = status;
  }
}

/** A token is renewed this long before it expires, so a request never carries a dying one. */
const RENEW_BEFORE_MS = 60_000;

/**
 * TestRun's address and a short-lived token from the TMS API. Kept in the query
 * cache, so signing out (which clears the cache) also drops the token.
 */
export const automationAccess = async (
  queryClient: QueryClient,
  renew = false,
): Promise<AutomationAccessDto> => {
  const cached = queryClient.getQueryData<AutomationAccessDto>(queryKeys.automation.access);
  if (cached && !renew && Date.parse(cached.expiresAt) - Date.now() > RENEW_BEFORE_MS) {
    return cached;
  }
  return queryClient.fetchQuery({
    queryKey: queryKeys.automation.access,
    queryFn: ({ signal }) => api.get<AutomationAccessDto>('/automation/access', undefined, signal),
    staleTime: 0,
  });
};

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: unknown }).name === 'AbortError';

/**
 * The browser talks to TestRun directly, with the token instead of cookies.
 * A rejected token is renewed and the request repeated once; it never signs the
 * user out of TMS — a TestRun 401 says nothing about the TMS session.
 */
export const automationRequest = async <T>(
  queryClient: QueryClient,
  path: string,
  init: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    signal?: AbortSignal;
  } = {},
  renewed = false,
): Promise<T> => {
  const { method = 'GET', body, signal } = init;
  const access = await automationAccess(queryClient, renewed);

  let response: Response;
  try {
    response = await fetch(`${access.apiUrl}/api${path}`, {
      method,
      credentials: 'omit',
      headers: {
        authorization: `Bearer ${access.token}`,
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (isAbortError(cause)) throw cause;
    throw new AutomationError('', 0);
  }

  if (response.status === 401 && !renewed) {
    return automationRequest<T>(queryClient, path, init, true);
  }
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: unknown } | null;
    const message =
      typeof payload?.error === 'string' ? payload.error : `TestRun: HTTP ${response.status}`;
    throw new AutomationError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
};
